import "./LoadoutDetails.css"
import { LoadOutDetail, LoadoutDetailsType, LoadOutItem, RouteInfo } from "../../types/types";
import { useCallback, useEffect, useState, useMemo } from "react";
import CustomDataGrid from "../../components/dms_common/CustomDataGrid";
import { GridRenderCellParams } from "@mui/x-data-grid";
import RenderSvg from "../../components/renderSvg/RenderSvg";
import { fetchLoadoutDetailsWithLoadNumber, getSalesReturnDetails, ristrictForwardNavigation, getLoadoutReqBody, updateLoadoutDetailsStatusInDb } from "./DeliveryConfirmationService";
import { useLocation, useNavigate } from "react-router-dom";
import { reInitialiseCollectionData, setCollectionDisableEditing, setDeliveryStatus, setReturnDetails } from "../../stateManagement/actions/collectionActions";
import { Loader } from "../../components/loader/Loader";
import FormHeader from "../../components/dms_common/FormHeader";
import { ChannelkartNetworkPut } from "../../services/networkService";
import { openPopup } from "../../stateManagement/actions/popupActions";
import { logLoadTrace } from "../../firebase";
import { getSalesDataByInvoiceNumber } from "../../services/manageOrderService";
import ScreenOverlay from "../../components/dms_common/ScreenOverlay";
import { getReturnableInvoiceDetails } from "../SalesReturn/SalesReturnService";
import StatusDropdown from "../../components/dms_common/StatusDropdown";
import MultiCategoryFilter from "../../components/dms_common/MultiCategoryFilter";
import { sendSlackAlert, SlackChannel } from "../../services/slackService";
import { safeStringify } from "../../components/dms_common/DmsUtils";
import { errorCodeToMessageMap } from "../../components/loadouts/pre-seller/PresellerUtils";

const invoiceDeliveryStatusMapping = [
    {
        id: "INVOICED",
        dmsLabelColor: "#CC911F",
        dmsLabel: "Pending",
        dmsColor: "#FFF9EF",
    },
    {
        id: "DELIVERED",
        dmsLabelColor: "#029847",
        dmsLabel: "Delivered",
        dmsColor: "#EAFFF4",
    },
    {
        id: "CANCELLED",
        dmsLabelColor: "#C44C36",
        dmsLabel: "Cancelled",
        dmsColor: "#FFEFF0",
    },
    {
        id: "PARTIAL DELIVERED",
        dmsLabelColor: "#17B4A3",
        dmsLabel: "Partially Delivered",
        dmsColor: "#F0FBFF",
    },
];

export const reviewStatusFilterMenuItems = [
    {
        id: "ALL",
        label: "All Delivery Confirmation"
    },
    {
        id: "INVOICED",
        label: "Invoiced"
    },
    {
        id: "DELIVERED",
        label: "Delivered"
    },
    {
        id: "CANCELLED",
        label: "Cancelled"
    },
    {
        id: "PARTIAL DELIVERED",
        label: "Partially Delivered"
    },
]

const LoadoutDetails = () => {
    const [checkedIdArray, setCheckedIdArray] = useState<string[]>([]);
    const [selectableRowIds, setSelectableRowIds] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
    const [loadoutDetails, setLoadoutDetails] = useState<LoadoutDetailsType>();
    const [invoiceList, setInvoiceList] = useState<LoadOutDetail[]>([]);
    const [filterOpen, setFilterOpen] = useState<boolean>(false);
    const [selectedFilterType, setSelectedFilterType] = useState<string>("status");
    const [preSelectedRowIds, setPreSelectedRowIds] = useState<string[]>([]);
    const [routeIdAndName, setRouteIdAndName] = useState<{[key: string]: any}>({});
    const filterData = (
      invoiceList: LoadOutDetail[],
      selectedKeyAccountStatuses: string[],
      selectedRoutes: string[],
      selectedSalesReps: string[],
      selectedOutlets: string[]
    ) => {
      let updatedData = [...invoiceList];
      if (selectedKeyAccountStatuses.length > 0 && !selectedKeyAccountStatuses.includes("ALL")) {
        updatedData = updatedData.filter(item => {
          const itemStatus = item.salesInfo?.invoiceStatus || item.invoiceStatus;
          return selectedKeyAccountStatuses.some(status =>
            itemStatus?.toLowerCase() === status.toLowerCase()
          );
        });
      }
      if (selectedRoutes.length > 0) {
        updatedData = updatedData.filter(item =>
          selectedRoutes.some(route => routeIdAndName[route]?.routeCode== item.routeCode)
        );
      }
      if (selectedSalesReps.length > 0) {
        updatedData = updatedData.filter(item =>
            item.salesmanId != null && selectedSalesReps.includes(String(item.salesmanId))
        );
      }
      if (selectedOutlets.length > 0) {
        updatedData = updatedData.filter(item =>
          selectedOutlets.includes(item.outletCode)
        );
      }
      return updatedData;
    };

    const [selectedKeyAccountStatuses, setSelectedKeyAccountStatuses] = useState<string[]>([]);
    const [selectedRoutes, setSelectedRoutes] = useState<string[]>([]);
    const [selectedSalesReps, setSelectedSalesReps] = useState<string[]>([]);
    const [selectedOutlets, setSelectedOutlets] = useState<string[]>([]);
    const filteredData = useMemo(() => {
      return filterData(invoiceList, selectedKeyAccountStatuses, selectedRoutes, selectedSalesReps, selectedOutlets);
    }, [invoiceList, selectedKeyAccountStatuses, selectedRoutes, selectedSalesReps, selectedOutlets]);
    const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") || "{}");
    const {fetchPricingBySalesrep} = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
    const [filterSearchValue, setFilterSearchValue] = useState<string>("");

    const location:{state:any} = useLocation();
    const navigate = useNavigate();


    const fetchData = async () => {
        setIsLoading(true);
        try {
            if (!location.state || !location.state.loadNumber) {
                navigate(-1);
                return;
            }
            
            const loadoutData: LoadoutDetailsType = await fetchLoadoutDetailsWithLoadNumber(
                location.state.loadOutDate, 
                location.state.loadNumber
            );
            
            const { routeIdAndNameMap } = await getReturnableInvoiceDetails(fetchPricingBySalesrep);
            
            setRouteIdAndName(routeIdAndNameMap);

            setLoadoutDetails(loadoutData);
            
            setInvoiceList(loadoutData.loadOutDetails);
            
            const selectableIds = loadoutData.loadOutDetails
                .filter(loadout => {
                    const status = loadout.salesInfo?.invoiceStatus || loadout.invoiceStatus;
                    return status?.toLowerCase() === "invoiced";
                })
                .map(loadout => loadout.id);
            setSelectableRowIds(selectableIds);
            setPreSelectedRowIds(loadoutData.loadOutDetails.filter(loadout => loadout.salesInfo.invoiceStatus.toLowerCase() != "invoiced").map(loadout => loadout.id));
            setCheckedIdArray(loadoutData.loadOutDetails.filter(loadout => loadout.salesInfo.invoiceStatus.toLowerCase() == "invoiced" && loadout.invoiceDraftStatus != "PARTIAL DELIVERED").map(loadout => loadout.id));
            setIsLoading(false);
            
        } catch (error) {
            console.error("Error fetching loadout details:", error);
            openPopup("Error", "Failed to fetch loadout details");
        } finally {
            setIsLoading(false);
        }
    };

    const handleStatusChange = (rowId: string, newStatus: string) => {
        loadoutDetails?.loadOutDetails.forEach(loadout => {
            if(loadout.id == rowId){
                loadout.invoiceDraftStatus = newStatus;
            }
        });
        setInvoiceList(prev => prev.map(invoice => invoice.id == rowId ? {...invoice, invoiceDraftStatus: newStatus} : invoice));
        if(newStatus.toLowerCase() == "partial delivered"){
            // handleRedirect("invoiceDetails", loadoutDetails?.loadOutDetails.find(loadout => loadout.id == rowId) as LoadOutDetail);
            setCheckedIdArray(prev => prev.filter(id => id != rowId));

        }else{
            setCheckedIdArray(prev => [...prev, rowId]);
        }
        updateLoadoutDetailsStatusInDb(location.state.loadNumber, rowId, newStatus);   
    };


    useEffect(() => {
        logLoadTrace(fetchData,"loadout_delivery_confirmation_invoice_list_page_load_time");
        ristrictForwardNavigation(navigate);
        delete localStorage.ristrictForwardNavigation;
    }, [])

    const columnsMetaData = {
        columns: [
            {
                headerName: "Invoice No.",
                headerParts: { top: "Invoice", bottom: "No." },
                mappedValue: "invoiceSerialNumber", //"invoiceNumber",
                width: 10
            },
            {
                headerName: "Delivery Date",
                headerParts: { top: "Delivery", bottom: "Date" },
                mappedValue: "deliveryDate",
                width: 12,
            },
            {
                headerName: "Outlet Name & Code",
                headerParts: { top: "Outlet Name", bottom: "& Code" },
                width: 13,
                mappedValue: "outletCode",
                cellRenderElement: (params: GridRenderCellParams) => (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <div style={{ overflow: "hidden", textOverflow: "ellipsis", width: "9rem", whiteSpace: "nowrap" }}>
                            {params?.row?.outletName}
                        </div>
                        <div style={{ display: "flex", gap: "6px" }}>
                            <div>{params?.row?.outletCode}</div>
                            {params?.row?.gstApplicable && <div style={{ height: "12px", marginTop: "1px" }}><RenderSvg name='gstIcon' /></div>}
                        </div>
                    </div>
                )
            },
            {
                headerName: "SKUs",
                mappedValue: "skuCount",
                width: 6,
            },

            {
                headerName: "Mode of Delivery",
                headerParts: { top: "Mode of", bottom: "Delivery" },
                mappedValue: "modeOfDelivery",
                width: 10,
            },
            {
                headerName: "Salesrep",
                headerParts: { top: "Sales", bottom: "Rep" },
                mappedValue: "salesmanId",
                width: 10,
            },
            // {
            //     headerName: "Amount",
            //     mappedValue: "totalAmount",
            //     width: 8,
            //     currencySymbol: "₹",
            // },
            // {
            //     headerName: "Tax Amount",
            //     headerParts: { top: "Tax", bottom: "Amount" },
            //     mappedValue: "taxAmount",
            //     width: 8,
            //     amountDisplay: true,
            // },
            // {
            //     headerName: "Discount Amount",
            //     headerParts: { top: "Discount", bottom: "Amount" },
            //     mappedValue: "discountAmount",
            //     width: 8,
            //     currencySymbol: "₹",
            // },
            // {
            //     headerName: "Final Amount",
            //     headerParts: { top: "Final", bottom: "Amount" },
            //     mappedValue: "finalAmount",
            //     width: 8,
            //     currencySymbol: "₹",
            // },
            {
                headerName: "Invoiced Qty",
                headerParts: { top: "Invoiced", bottom: "Qty" },
                mappedValue: "totalCaseQty,totalPieceQty,totalOtherQty",
                width: 12,
                headerAlign: "left",
                align: "left",
                type: "stocks",
                qtyDisplay:true
              },
              {
                headerName: "Delivered Qty",
                headerParts: { top: "Delivered", bottom: "Qty" },
                mappedValue: "soldCaseQty,soldPieceQty,soldOtherQty",
                width: 12,
                headerAlign: "left",
                align: "left",
                type: "stocks",
                qtyDisplay:true
              },
              {
                headerName: "Return Qty",
                headerParts: { top: "Return", bottom: "Qty" },
                mappedValue: "totalCaseLeftQty,totalPieceLeftQty,totalOtherLeftQty",
                width: 12,
                headerAlign: "left",
                align: "left",
                type: "stocks",
                qtyDisplay:true
              },
            {
                headerName: "Status",
                mappedValue: "invoiceStatus",
                cellRenderElement: (params: GridRenderCellParams) => (
                    <StatusDropdown 
                        id={params.row.id}
                        isDisabled={params.row.invoiceStatus.toLowerCase() != "invoiced"}
                        invoiceDraftStatus={params.row.invoiceDraftStatus}
                        onStatusChange={handleStatusChange}
                        statusConfig={invoiceDeliveryStatusMapping}
                    />
                ),
                width: 14
            },
        ],
        Actions: ["redirect"],
    };

    const getOutletFilterOptions = (invoiceList: LoadOutDetail[]) => {
        const outletMap: { [code: string]: { outletName: string; count: number; outletCode: string } } = {};

        invoiceList.forEach(invoice => {
            if (!invoice.outletCode) return;
            if (!outletMap[invoice.outletCode]) {
                outletMap[invoice.outletCode] = {
                    outletName: invoice.outletName,
                    count: 1,
                    outletCode: invoice.outletCode,
                };
            } else {
                outletMap[invoice.outletCode].count += 1;
            }
        });

        const outletNameCount: { [name: string]: number } = {};
        Object.values(outletMap).forEach(({ outletName }) => {
            outletNameCount[outletName] = (outletNameCount[outletName] || 0) + 1;
        });

        return Object.values(outletMap).map(({ outletName, count, outletCode }) => {
            let label = outletName;
            if (outletNameCount[outletName] > 1) {
                label = `${outletName}-${outletCode}`;
            }
            return { id: outletCode, label, count };
        });
    };

    const filters = [
        {
            id: 'status',
            label: 'Status',
            options: reviewStatusFilterMenuItems.map(status => ({
                id: status.id,
                label: status.label,
                count: invoiceList.filter(invoice => {
                    if (status.id === "ALL") return true;
                    return invoice.salesInfo?.invoiceStatus?.toLowerCase() === status.id.toLowerCase();
                }).length
            })),
            selectedOptions: selectedKeyAccountStatuses,
            setSelectedOptions: setSelectedKeyAccountStatuses,
        },
        fetchPricingBySalesrep && {
            id: 'beat',
            label: 'Route',
            options: Object.keys(routeIdAndName).map(routeKey => ({
                id: String(routeKey),
                label: String(routeKey),
                count: invoiceList.filter(invoice => invoice.routeCode === String(routeIdAndName[routeKey]?.routeCode)).length
            })),
            selectedOptions: selectedRoutes,
            setSelectedOptions: setSelectedRoutes,
        },
        {
            id: 'salesrep',
            label: 'Salesrep',
            options: Array.from(new Set(invoiceList.map(inv => inv.salesmanId).filter(rep => rep != null && rep !== "null" && rep !== "undefined"))).map(rep => ({
                id: String(rep),
                label: String(rep),
                count: invoiceList.filter(inv => inv.salesmanId === rep).length
            })),
            selectedOptions: selectedSalesReps,
            setSelectedOptions: setSelectedSalesReps,
        },
        {
            id: 'outlet',
            label: 'Outlet',
            options: getOutletFilterOptions(invoiceList),
            selectedOptions: selectedOutlets,
            setSelectedOptions: setSelectedOutlets,
                }
    ].filter(Boolean);



    const getLoadoutItemMapKey = (item:LoadOutItem) => {
        const batchIdConfiguration = ConfigDictionary.batchIdConfiguration;
        return batchIdConfiguration ? item.batchCode+"_"+item.batchId : item.batchCode;
    }

    const handleRedirect = async (redirectUrl: string, rowData: LoadOutDetail) => {
        setIsRedirecting(true);
        try{
            reInitialiseCollectionData();
            delete localStorage.deliveryCollectionData;
            const loadoutItemMap: {[key:string]:LoadOutItem} = {};
            rowData.loadOutItems.forEach((item:LoadOutItem) => {
                const loadoutItemMapKey = getLoadoutItemMapKey(item);
                if(!loadoutItemMap[loadoutItemMapKey]){
                    loadoutItemMap[loadoutItemMapKey] = {...item};
                }else{
                    loadoutItemMap[loadoutItemMapKey].caseQty += item.caseQty;
                    loadoutItemMap[loadoutItemMapKey].pieceQty += item.pieceQty;
                    loadoutItemMap[loadoutItemMapKey].otherQty += item.otherQty;
                    loadoutItemMap[loadoutItemMapKey].pieceQtyLeft += item.pieceQtyLeft;
                    loadoutItemMap[loadoutItemMapKey].caseQtyLeft += item.caseQtyLeft;
                    loadoutItemMap[loadoutItemMapKey].otherQtyLeft += item.otherQtyLeft;
                }
                
            })
            const { returnDetailsArray, returnDetailsObject, returnSalesDetails } = await getSalesReturnDetails(rowData.invoiceNumber, loadoutItemMap);
            if (returnDetailsObject) {
                setReturnDetails(returnDetailsObject);
            }
            const currentStatus = rowData.salesInfo?.invoiceStatus || rowData.invoiceStatus || "";
            if (currentStatus.toLowerCase() != "invoiced") {    //rowData.salesInfo?.invoiceStatus?.toLowerCase()
                setCollectionDisableEditing(true);
            }
            const invoice = await getSalesDataByInvoiceNumber(rowData.invoiceNumber);
            const isEInvoiced = invoice.extendedAttributes?.IRNDtls;
            // const isEInvoiced = true;
            // setCollectionDisableEditing(isEInvoiced ? false : true);
            setDeliveryStatus(isEInvoiced ? "DELIVERED" : rowData.invoiceStatus);
            navigate(redirectUrl, {
                state: {
                    loadoutData: loadoutDetails,
                    invoiceDetails: {...rowData, isEInvoiced, hasGstin:invoice.hasGstin, extendedAttributes: {...invoice.extendedAttributes,isNewSchemes:true}, loginId:invoice.loginId, source:invoice.source},
                    returnDetailsArray: returnDetailsArray,
                    isPartialDeliveryAllowed:location.state.isPartialDeliveryAllowed,
                    type: "loadout"
                },
            });
        }catch(e){
            openPopup("Error","Unable to open invoice");
            setIsRedirecting(false);
        }
    };

    

    const deliverSelectedInvoices = async () => {
        
        if(!loadoutDetails || checkedIdArray.length == 0){
            return;
        }
        setIsSubmitting(true);
        setIsLoading(true);
        let reqBody: any = null;
        try{
            reqBody = await getLoadoutReqBody(loadoutDetails,checkedIdArray);
            const response = await ChannelkartNetworkPut("/v1/loadoutOrchestrator",reqBody);
            
            if(response.status!=200){
                openPopup("Error","Failed to make status delivered for invoices: " + checkedIdArray);
                setIsSubmitting(false);
                return;
            }
       
            setCheckedIdArray([]);
            await fetchData();
            ristrictForwardNavigation(navigate);
            openPopup("Success","Invoice(s) delivered successfully");
        }catch(e){
            console.error("Error in delivering invoices:", e);
            
            let errorMessage = "Failed to update delivery status. Please try again.";
            
            // Handle loadoutOrchestrator specific error responses
            const stepResponses = (e as any)?.response?.data?.stepResponses;
            if (stepResponses) {
                // Find the first failed step and get its error message
                for (const [stepName, stepResponse] of Object.entries(stepResponses)) {
                    const typedStepResponse = stepResponse as any;
                    if (typedStepResponse.Status === "Failed") {
                        const errorCode = typedStepResponse.ErrorCode;
                        const stepErrorKey = `${stepName}-ERROR${errorCode}`;
                        
                        if ((errorCodeToMessageMap as any)[stepErrorKey]) {
                            // Use predefined user-friendly message from map
                            errorMessage = (errorCodeToMessageMap as any)[stepErrorKey];
                        } else if (typedStepResponse.ErrorMessage) {
                            // Use error message from response, removing any extra quotes
                            errorMessage = typedStepResponse.ErrorMessage
                                .replace(/^["'](.*)["']$/, '$1') // Remove leading/trailing quotes
                                .replace(/\\"/g, '"'); // Unescape inner quotes
                        }
                        break;
                    }
                }
            }
            openPopup("Error", errorMessage);
            const errorApiResponse = (e as any)?.response || null;
            await sendSlackAlert( 
                SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
                "Loadout: Failed to deliver invoice while deliverSelectedInvoices",
                `Loadout: Failed to deliver invoice while deliverSelectedInvoices in LoadoutDetails.tsx`,
                safeStringify({ requestBody: reqBody, error: e, apiResponse: errorApiResponse })
            );
        }finally{
            setIsSubmitting(false);
            setIsLoading(false);
        }
    };

    const submitFooter = () => {
        return (
            <div className="loadout-multi-select-submit-button-container">
                <button
                    className={`loadout-multi-select-submit ${checkedIdArray.length == 0 || isSubmitting ? 'disabled' : ''}`}
                    onClick={()=>{logLoadTrace(deliverSelectedInvoices,"multi_invoice_deliver_submit_time")}}
                >
                    {isSubmitting ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: '20px', height: '20px' }}>
                                <Loader />
                            </div>
                            <span className="loadout-multi-select-submit-text">
                                Updating...
                            </span>
                        </div>
                    ) : (
                        <span className="loadout-multi-select-submit-text">
                            Update Delivery Status
                        </span>
                    )}
                </button>
            </div>
        );
    }

    const updateCheckedIdArray = (selectedIds: any[]) => {
        const invariantIds = checkedIdArray.filter(checkedId => !selectableRowIds.includes(checkedId));
        const updatedCheckedIdArray = Array.from(new Set([...invariantIds, ...selectedIds]))
        setCheckedIdArray(updatedCheckedIdArray);
    }

    const handleSearchValueChange = useCallback((searchText: string) => {
        const searchLower = searchText.toLowerCase();
        const updatedList = loadoutDetails ? loadoutDetails.loadOutDetails.filter(invoice =>
            (invoice.invoiceSerialNumber || invoice.invoiceNumber)?.toLowerCase().includes(searchLower) ||
            invoice.outletCode.toLowerCase().includes(searchLower) ||
            invoice.outletName.toLowerCase().includes(searchLower)
        ) : [];
        
        setInvoiceList(updatedList);
        
        const selectableIds = updatedList
            .filter(data => {
                const status = data.salesInfo?.invoiceStatus || data.invoiceStatus;
                return status?.toLowerCase() === "invoiced";
            })
            .map(data => data.id);
        setSelectableRowIds(selectableIds);
        
        setCheckedIdArray(prev => prev.filter(id => selectableIds.includes(id)));
        
        //combne or reset filter 
        setSelectedKeyAccountStatuses([]);
        setSelectedRoutes([]);
        setSelectedSalesReps([]);
    }, [loadoutDetails]);

    return (

        <div className='loadout-details-container'>
            {/* <ScreenOverlay apply={isRedirecting || isSubmitting}/> */}
            <div className = "loadout-details-header">
            <FormHeader
                parentLabel={`Loadout No. ${location.state.loadNumber}`}
                label={"Invoice List"}
                goBackToParent={() => navigate(-1)}
                searchFunction={handleSearchValueChange}
                Component={
                    <MultiCategoryFilter
                      isOpen={filterOpen}
                      setIsOpen={setFilterOpen}
                      activeFilter={selectedFilterType}
                      setActiveFilter={setSelectedFilterType}
                      filters={filters}
                      label="Type & Status"
                      searchValue={filterSearchValue}
                      onSearchChange={setFilterSearchValue}
                      showSearch={true}
                    />
                }
            />
            </div>
            {isLoading ?
                <Loader />
                :
                <CustomDataGrid
                    // rows = {Array.isArray(loadoutDetails.loadOutDetails) ? loadoutDetails.loadOutDetails : []}
                    rows={filteredData}
                    columnsMetaData={columnsMetaData}
                    tableWidth={0.78}
                    redirectUrl="invoiceDetails"
                    redirectUrlFunction={handleRedirect}
                    orderStatusConfig={invoiceDeliveryStatusMapping}
                    isCheckboxSelection={true}
                    disabledRowIds={invoiceList.filter(invoice => invoice.invoiceDraftStatus != "PARTIAL DELIVERED" && invoice.invoiceStatus.toLowerCase() == "invoiced").map(invoice => invoice.id)}
                    checkedIdArray={[...checkedIdArray,...preSelectedRowIds]}
                    rowIdsWithCheckboxHidden={[...preSelectedRowIds, ...invoiceList.filter(invoice => invoice.invoiceDraftStatus == "PARTIAL DELIVERED").map(invoice => invoice.id)]}
                    setCheckedIdArray={updateCheckedIdArray}
                    selectableRowIds={selectableRowIds}
                    footerElement={submitFooter}
                />
            }
        </div>
    );
}


export default LoadoutDetails;